import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableHead, TableRow,
  TableCell, TableBody, IconButton, Tooltip, Button, Alert, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Switch, FormControlLabel, Divider
} from '@mui/material';
import { Add, Edit, Delete, ArrowBack, Business, WarningAmber } from '@mui/icons-material';
import axios from 'axios';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';

const API = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const emptyForm = { code: '', name: '', description: '', isActive: true };

const DepartmentList = () => {
  const navigate = useNavigate();
  const [depts,   setDepts]   = useState([]);
  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState('');
  const [open,    setOpen]    = useState(false);
  const [editing, setEditing] = useState(null);
  const [form,    setForm]    = useState(emptyForm);
  const { attemptClose, discardDialog } = useUnsavedGuard({ open, value: form, onClose: () => setOpen(false) });
  const [saving,     setSaving]     = useState(false);
  const [formErr,    setFormErr]    = useState('');
  const [confirmDlg, setConfirmDlg] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/employees/departments/all`, { headers: hdrs() });
      setDepts(data);
    } catch { setError('Failed to load departments'); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditing(null); setForm(emptyForm); setFormErr(''); setOpen(true); };
  const openEdit = (d) => {
    setEditing(d);
    setForm({ code: d.code, name: d.name, description: d.description || '', isActive: d.isActive !== false });
    setFormErr(''); setOpen(true);
  };

  const handle = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const handleSave = async () => {
    if (!form.code.trim()) { setFormErr('Code is required'); return; }
    if (!form.name.trim()) { setFormErr('Name is required'); return; }
    setSaving(true); setFormErr('');
    try {
      if (editing) {
        await axios.put(`${API}/api/employees/departments/${editing.code}`, form, { headers: hdrs() });
      } else {
        await axios.post(`${API}/api/employees/departments`, form, { headers: hdrs() });
      }
      setOpen(false);
      setSuccess(editing ? 'Updated' : 'Created');
      setTimeout(() => setSuccess(''), 3000);
      load();
    } catch (err) {
      setFormErr(err.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  const confirmAction = async () => {
    const dept = confirmDlg;
    setConfirmDlg(null);
    try {
      await axios.delete(`${API}/api/employees/departments/${dept.code}`,
        { headers: hdrs(), data: { name: dept.name } });
      setSuccess('Deleted'); load();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setError(err.response?.data?.error || 'Delete failed'); }
  };

  const active = depts.filter(d => d.isActive !== false).length;

  return (
    <Box sx={{ maxWidth: 860, mx: 'auto', mt: 4, mb: 4 }}>
      <Card elevation={3}>
        <CardContent>

          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <Button startIcon={<ArrowBack />} size="small" onClick={() => navigate('/hr')}>Back</Button>
              <Business sx={{ color: '#546e7a' }} />
              <Typography variant="h5" fontWeight={700} sx={{ color: '#37474f' }}>Departments</Typography>
              <Chip label={`${active} active`} size="small" color="success" />
              <Chip label={depts.length} size="small" variant="outlined" />
            </Box>
            <Button variant="contained" startIcon={<Add />} onClick={openAdd}
              sx={{ bgcolor: '#546e7a', '&:hover': { bgcolor: '#37474f' } }}>
              Add Department
            </Button>
          </Box>

          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          {error   && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

          <Divider sx={{ mb: 2 }} />

          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell><strong>Code</strong></TableCell>
                <TableCell><strong>Name</strong></TableCell>
                <TableCell><strong>Description</strong></TableCell>
                <TableCell align="center"><strong>Status</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {depts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="text.secondary" py={3}>
                      No departments. Defaults are seeded automatically on first use.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : depts.map(dept => (
                <TableRow key={dept.code} hover sx={{ '&:nth-of-type(odd)': { bgcolor: 'grey.50' } }}>
                  <TableCell>
                    <Chip label={dept.code} size="small" variant="outlined"
                      sx={{ fontFamily: 'monospace', fontWeight: 700 }} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}
                      color={dept.isActive === false ? 'text.disabled' : 'text.primary'}>
                      {dept.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">{dept.description || '—'}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={dept.isActive !== false ? 'Active' : 'Inactive'} size="small"
                      color={dept.isActive !== false ? 'success' : 'default'} />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" color="primary" onClick={() => openEdit(dept)}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => setConfirmDlg(dept)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

        </CardContent>
      </Card>

      {/* Confirm Delete Dialog */}
      <Dialog open={Boolean(confirmDlg)} onClose={() => setConfirmDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
          <WarningAmber color="error" /> Delete Department
        </DialogTitle>
        <DialogContent>
          <Typography>
            Delete department <strong>{confirmDlg?.name}</strong> ({confirmDlg?.code})?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDlg(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmAction}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog ── */}
      {discardDialog}
      <Dialog open={open} onClose={attemptClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? `Edit — ${editing.name}` : 'Add Department'}</DialogTitle>
        <DialogContent dividers>
          {formErr && <Alert severity="error" sx={{ mb: 2 }}>{formErr}</Alert>}
          <Box display="flex" flexDirection="column" gap={2}>
            <Box display="flex" gap={2}>
              <TextField label="Code *" name="code" value={form.code}
                onChange={handle} size="small" sx={{ width: 130 }}
                disabled={!!editing}
                helperText={editing ? 'Cannot change' : 'e.g. TEACH, FIN'}
                inputProps={{ style: { textTransform: 'uppercase' } } } />
              <TextField label="Department Name *" name="name" value={form.name}
                onChange={handle} size="small" fullWidth />
            </Box>
            <TextField label="Description" name="description" value={form.description}
              onChange={handle} size="small" fullWidth multiline rows={2}
              helperText="Optional — shown in reports and tooltips" />
            {editing && (
              <FormControlLabel
                control={
                  <Switch checked={form.isActive}
                    onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} />
                }
                label="Active (inactive departments are hidden from employee dropdowns)" />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={attemptClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}
            sx={{ bgcolor: '#546e7a', '&:hover': { bgcolor: '#37474f' } }}>
            {saving ? 'Saving…' : editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default DepartmentList;
